import { useState } from 'react';
import Head from '@/components/feature/Head';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    if (charCount > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const res = await fetch('https://readdy.ai/api/form/d9u3o2l3pcjqs2fcslf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于H漫画',
    description: 'H漫画 - 最新H漫画在线阅读平台，致力于为成年用户提供优质H漫画阅读体验。',
    url: 'https://hahamanga.github.io/about',
    publisher: { '@type': 'Organization', name: 'H漫画' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'H漫画平台的漫画资源多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'H漫画平台每日更新最新H漫画内容，我们的编辑团队全天候追踪热门漫画资源，确保读者能在第一时间阅读到最新更新的漫画章节。',
        },
      },
      {
        '@type': 'Question',
        name: 'H漫画平台支持哪些阅读方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'H漫画平台支持在线网页阅读和移动端阅读，适配各种设备屏幕尺寸，为读者提供流畅的H漫画在线阅读体验。同时部分漫画资源支持下载离线阅读。',
        },
      },
      {
        '@type': 'Question',
        name: 'H漫画平台是否需要注册才能阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'H漫画平台大部分内容支持游客直接在线阅读，注册会员后可享受收藏、历史记录、评论互动等更多功能。',
        },
      },
      {
        '@type': 'Question',
        name: 'H漫画平台上的漫画有哪些分类？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'H漫画平台涵盖日系漫画、韩系漫画、国产原创、同人志、轻熟女、纯爱系、剧情向、奇幻冒险、校园青春、职场OL、合集系列等多种分类，满足不同读者的喜好。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系H漫画平台的客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过页面底部的联系我们表单、发送邮件至contact@hmanga.com、或添加客服QQ：1234567890与我们取得联系，工作时间9:00-21:00。',
        },
      },
    ],
  };

  return (
    <>
      <Head
        title="关于我们 - H漫画 | 品牌介绍与联系方式"
        description="了解H漫画的品牌故事与发展历程。H漫画是领先的H漫画在线阅读平台，致力于为成年用户提供优质、安全的H漫画阅读体验。"
        keywords="H漫画,关于H漫画,H漫画平台,H漫画在线阅读,联系我们"
        canonical="https://hahamanga.github.io/about"
        ogTitle="关于我们 - H漫画 | 品牌介绍与联系方式"
        ogDescription="了解H漫画的品牌故事与发展历程。"
        ogUrl="https://hahamanga.github.io/about"
        jsonLd={[aboutSchema, faqSchema]}
      />

      <div className="min-h-screen pt-20 pb-10">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Hero */}
          <section className="mb-14 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-100 mb-4">
              关于 <span className="text-primary-500">H漫画</span>
            </h1>
            <p className="text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
              为每一位成年漫画爱好者打造最优质的在线阅读平台
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">品牌介绍</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center mb-4">
                  <i className="ri-lightbulb-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-200 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  H漫画致力于打造国内领先的H漫画在线阅读平台，为成年用户提供海量优质H漫画资源。我们坚信每位读者都应当享有流畅、安全、高品质的漫画阅读体验。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-lg text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-200 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  成为全球最受欢迎的H漫画阅读平台，汇聚各国优秀漫画作品，构建一个连接创作者与读者的多元化漫画生态社区。
                </p>
              </div>
            </div>
          </section>

          {/* Story */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">发展历程</h2>
            <div className="space-y-6">
              {[
                { year: '2024', title: '平台创立', desc: 'H漫画平台正式上线，首批收录超过3,000部优质H漫画资源，涵盖日系、韩系、国产等多个品类。' },
                { year: '2025', title: '快速发展', desc: '平台月活跃用户突破300万，漫画资源库扩充至8,000+部，新增同人志、轻熟女等特色分类频道。' },
                { year: '2026', title: '全面升级', desc: '完成平台全面技术升级，优化阅读体验与页面加载速度。资源总量突破10,000部，月活跃用户超过500万，成为行业领先的H漫画在线阅读平台。' },
              ].map((item) => (
                <div key={item.year} className="flex gap-6">
                  <div className="flex-shrink-0 w-16 text-right">
                    <span className="text-sm font-bold text-primary-500">{item.year}</span>
                  </div>
                  <div className="flex-1 pb-6 border-l-2 border-background-200 pl-6">
                    <h4 className="text-base font-semibold text-foreground-200 mb-2">{item.title}</h4>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Stats / Reputation */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">平台数据</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50 text-center">
                <p className="text-3xl font-bold text-primary-500 mb-1">10,000+</p>
                <p className="text-sm text-foreground-500">漫画资源</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50 text-center">
                <p className="text-3xl font-bold text-accent-500 mb-1">500万+</p>
                <p className="text-sm text-foreground-500">月活跃用户</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50 text-center">
                <p className="text-3xl font-bold text-primary-500 mb-1">每日更新</p>
                <p className="text-sm text-foreground-500">从不间断</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/50 text-center">
                <p className="text-3xl font-bold text-accent-500 mb-1">99.9%</p>
                <p className="text-sm text-foreground-500">服务可用率</p>
              </div>
            </div>
          </section>

          {/* User Reviews */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: '漫画迷小王', rating: 5, text: 'H漫画平台资源非常丰富，更新速度超快！画质清晰，阅读体验流畅，是我每天必刷的漫画网站。强烈推荐！', date: '2026年8月' },
                { name: '资深读者Lily', rating: 5, text: '分类很细致，找漫画特别方便。日系和韩系漫画资源都很全，而且界面设计简洁大方，用起来很舒服。', date: '2026年7月' },
                { name: '二次元爱好者', rating: 4, text: '一直在用H漫画平台看漫画，资源更新及时，分类清晰。客服响应也很快，遇到问题能得到及时解决。', date: '2026年8月' },
              ].map((review) => (
                <div key={review.name} className="p-5 rounded-xl bg-background-100 border border-background-200/50">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-600'} text-sm w-4 h-4 flex items-center justify-center`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-300 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground-200">{review.name}</span>
                    <span className="text-xs text-foreground-500">{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">常见问题</h2>
            <div className="space-y-3">
              {[
                { q: 'H漫画平台的漫画资源多久更新一次？', a: 'H漫画平台每日更新最新H漫画内容，我们的编辑团队全天候追踪热门漫画资源，确保读者能在第一时间阅读到最新更新的漫画章节。' },
                { q: 'H漫画平台支持哪些阅读方式？', a: 'H漫画平台支持在线网页阅读和移动端阅读，适配各种设备屏幕尺寸，为读者提供流畅的H漫画在线阅读体验。同时部分漫画资源支持下载离线阅读。' },
                { q: 'H漫画平台是否需要注册才能阅读？', a: 'H漫画平台大部分内容支持游客直接在线阅读，注册会员后可享受收藏、历史记录、评论互动等更多功能。' },
                { q: '如何联系H漫画平台的客服？', a: '您可以通过页面下方的联系我们表单、发送邮件至contact@hmanga.com、或添加客服QQ：1234567890与我们取得联系，工作时间9:00-21:00。' },
              ].map((faq, idx) => (
                <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/50">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-200 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-100 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">电子邮件</p>
                      <p className="text-sm text-foreground-400">contact@hmanga.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-3-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">客服QQ</p>
                      <p className="text-sm text-foreground-400">1234567890</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">工作时间</p>
                      <p className="text-sm text-foreground-400">每日 9:00 - 21:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-200">安全承诺</p>
                      <p className="text-sm text-foreground-400">严格保护用户隐私，采用SSL加密传输</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="p-6 rounded-xl bg-background-100 border border-background-200/50 space-y-4"
                >
                  <h3 className="text-base font-semibold text-foreground-200 mb-2">发送留言</h3>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div>
                    <label htmlFor="contact-name" className="block text-xs text-foreground-400 mb-1.5">您的称呼</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的称呼"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-xs text-foreground-400 mb-1.5">电子邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-xs text-foreground-400 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-xs text-foreground-400 mb-1.5">
                      留言内容
                      <span className="ml-2 text-foreground-500">({charCount}/500)</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-300 text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字符）"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-400 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-400 text-center">{formError || '提交失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center p-8 rounded-2xl bg-background-100 border border-background-200/50">
            <h3 className="text-xl font-heading font-bold text-foreground-100 mb-3">准备好开始阅读了吗？</h3>
            <p className="text-sm text-foreground-400 mb-5">探索海量优质H漫画资源，发现你的下一部心头好</p>
            <Link
              to="/category/all"
              className="inline-flex px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              立即浏览
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}